/**
 * Composer autocomplete (Slack-style), shared by every platform. It finds the token typed before the caret
 * (`@` people, agents and broadcasts; `#` channels; `:` emoji after 2+ characters; `/` commands at the start of the
 * message), ranks suggestions, and builds the text edit that inserts the chosen token in the form the server parser
 * (apps/api/src/richtext/parse.ts) resolves: `@Display Name` when that name is unambiguous (else `<@userId>`),
 * `#channel-name` (else `<#channelId>`), `@here` / `@channel` / `@everyone`, the emoji itself, `/command`.
 *
 * Ranking reuses logic/search `matchScore` (exact > prefix > word start > substring > in-order fuzzy, diacritics
 * folded: "joao" finds "João"). Within a match tier: people in this conversation, then recent DM partners, then
 * everyone (me last). Directory filtering follows `can.seeDirectory` (logic/access), broadcasts `can.mentionBroadcast`.
 */
import type { AvailableCommand, Channel, Member, UserStatus } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { canSeeMember, capabilitiesFor, isRestricted, organizationName } from "./access.ts";
import { agentState, type LiveState } from "./live.ts";
import { activeStatus } from "./profile.ts";
import { fold, matchScore } from "./search.ts";
import { agentPresence, type AvatarPresence } from "./sidebar.ts";
import { activeGroupMemberIds, isGroupActive } from "./userGroups.ts";
import { isSupportMember, matchesMention, SUPPORT_HANDLE, SUPPORT_STORED_NAME } from "./support.ts";
import { sharedChannelIdsWith, type WorkspaceCache } from "./workspace.ts";

export type AutocompleteKind = "mention" | "channel" | "emoji" | "command";

/** The token being typed: the trigger character sits at `start`, the caret at `end`, `query` is what's between. */
export type AutocompleteTrigger = { kind: AutocompleteKind; start: number; end: number; query: string };

export type BroadcastRange = "here" | "channel" | "everyone";
export type SlashCommandId = "remind" | "status" | "invite" | "mute" | "task" | "page" | "huddle";

export type AutocompleteItem =
  | {
      kind: "user";
      id: string;
      label: string;
      /** Full name when it differs from the display name, else the title. */
      detail?: string;
      avatarUrl?: string;
      isAgent: boolean;
      /** Another organization ("External · Org" badge). */
      organization?: string;
      presence?: AvatarPresence;
      /** Their current custom status: the emoji after the name and its text in grey (logic/profile activeStatus). */
      status?: UserStatus;
    }
  | { kind: "broadcast"; id: BroadcastRange; label: string; description: MessageDescriptor }
  /** A user group: `label` = "@design", `name` = "Design team", `memberCount` = its active members. */
  | { kind: "group"; id: string; label: string; name: string; memberCount: number }
  | { kind: "channel"; id: string; label: string; detail?: string; isPrivate: boolean; external: boolean }
  | { kind: "emoji"; id: string; label: string; emoji: string }
  | { kind: "command"; id: SlashCommandId; label: string; icon: string; description: MessageDescriptor; usage?: MessageDescriptor }
  /** A slash command of an app installed here (contracts apps.ts AvailableCommand): `id` = label = "/deploy". */
  | { kind: "appCommand"; id: string; label: string; appName: string; description: string; usageHint?: string }
  /** A link workflow of this channel (Workflow Builder): choosing it runs it. `id` = the workflow id, `label` = its name. */
  | { kind: "workflow"; id: string; label: string; icon?: string; description?: string };

/** A channel's link workflow as the `/` menu lists it. */
export type WorkflowShortcut = { id: string; name: string; icon?: string | undefined; description?: string | undefined };

/** The open menu: the token [start, end) it replaces, its rows (at most MAX_ITEMS) and the highlighted row. */
export type AutocompleteMenuState = {
  kind: AutocompleteKind;
  start: number;
  end: number;
  query: string;
  items: AutocompleteItem[];
  active: number;
};

/** One emoji and its shortcodes (emojibase iamcal + GitHub, like the server parser). */
export type EmojiEntry = { unicode: string; names: string[] };

/** Where the message is written: its participants rank first; broadcasts only in channels. */
export type MentionScope = {
  channelId: string;
  /** Authors seen in this conversation, newest first. */
  recentAuthorIds: string[];
  now: number;
};

export type TextEdit = { text: string; caret: number };

export type CommandHint = { label: string; usage: MessageDescriptor };

export const MAX_ITEMS = 8;
/** Characters after ":" before emoji suggestions appear. */
export const EMOJI_MIN_QUERY = 2;
/** Longest query a trigger follows (beyond it the menu closes). */
export const MAX_QUERY = 32;
/** logic/search `matchScore` of a substring match (below it: in-order fuzzy). */
const SUBSTRING = 40;
/** DM conversations counted as "recent" for the ranking boost. */
const RECENT_DMS = 10;

export const BROADCASTS: BroadcastRange[] = ["here", "channel", "everyone"];

const BROADCAST_DESCRIPTIONS: Record<BroadcastRange, MessageDescriptor> = {
  here: msg("composer.broadcast.here"),
  channel: msg("composer.broadcast.channel"),
  everyone: msg("composer.broadcast.everyone"),
};

/** Built-in slash commands, in menu order (ARCHITECTURE §4: running them is a separate feature). */
export const SLASH_COMMANDS: { id: SlashCommandId; icon: string; description: MessageDescriptor; usage?: MessageDescriptor }[] = [
  { id: "remind", icon: "clock", description: msg("composer.command.remind.description"), usage: msg("composer.command.remind.usage") },
  { id: "status", icon: "emoji", description: msg("composer.command.status.description"), usage: msg("composer.command.status.usage") },
  { id: "invite", icon: "invite", description: msg("composer.command.invite.description"), usage: msg("composer.command.invite.usage") },
  { id: "mute", icon: "bellOff", description: msg("composer.command.mute.description"), usage: msg("composer.command.mute.usage") },
  { id: "task", icon: "todo", description: msg("composer.command.task.description"), usage: msg("composer.command.task.usage") },
  { id: "page", icon: "page", description: msg("composer.command.page.description"), usage: msg("composer.command.page.usage") },
  { id: "huddle", icon: "huddle", description: msg("composer.command.huddle.description") },
];

const WORD = /[\p{L}\p{N}_]/u;
const SPACE = /\s/u;
const CHANNEL_QUERY = /^[\p{L}\p{N}_-]*$/u;
/** Same characters as the server's `:shortcode:` rule. */
const EMOJI_QUERY = /^[a-z0-9_+\-']+$/i;
const COMMAND_QUERY = /^\/([a-z0-9_-]*)$/i;
/** A query made of name characters: an empty result shows "No matches" instead of closing the menu. */
const NAMEISH = /^[\p{L}\p{N}_'-]*$/u;
/** A channel name the server resolves as `#name`. */
const CHANNEL_NAME = /^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u;
const COMMAND_TEXT = /^\/([a-z0-9_-]+)\s/i;

const isWord = (c: string): boolean => c !== "" && WORD.test(c);
const isSpace = (c: string): boolean => c !== "" && SPACE.test(c);

// ---------- Triggers ----------

/** True when `index` is inside a ``` block or an inline `code` span (an unclosed backtick counts: code being typed). */
export function inCode(text: string, index: number): boolean {
  let fenced = false;
  let inline = false;
  let i = 0;
  while (i < index && i < text.length) {
    if (text.startsWith("```", i)) {
      fenced = !fenced;
      inline = false;
      i += 3;
      continue;
    }
    const ch = text.charAt(i);
    if (ch === "\n") inline = false;
    else if (ch === "`" && !fenced) inline = !inline;
    i += 1;
  }
  return fenced || inline;
}

/**
 * The trigger the caret is in, if any:
 * - `/query` only at the very start of the message (letters, digits, `_`, `-`);
 * - `@query`, `#query`, `:query` at the start of the text or after a space, newline or punctuation (never after a
 *   letter or digit, so `ana@acme.com` and `12:30` aren't triggers) and never inside code;
 * - `@` queries may hold single spaces ("@ada lov") but not a trailing one; `#` and `:` queries hold no spaces;
 *   `:` needs EMOJI_MIN_QUERY characters of a shortcode.
 */
export function findTrigger(text: string, caret: number): AutocompleteTrigger | undefined {
  if (caret < 0 || caret > text.length) return undefined;
  const head = text.slice(0, caret);
  const command = COMMAND_QUERY.exec(head);
  if (command !== null) return { kind: "command", start: 0, end: caret, query: command[1]! };
  let sawSpace = false;
  for (let i = caret - 1; i >= 0 && caret - i <= MAX_QUERY + 1; i--) {
    const ch = head.charAt(i);
    if (ch === "\n") return undefined;
    if (isSpace(ch)) {
      // A trailing space ends the token; two spaces in a row end a name.
      if (i === caret - 1 || isSpace(head.charAt(i + 1))) return undefined;
      sawSpace = true;
      continue;
    }
    if (ch !== "@" && ch !== "#" && ch !== ":") continue;
    if (sawSpace && ch !== "@") continue;
    const prev = i > 0 ? head.charAt(i - 1) : "";
    const query = head.slice(i + 1);
    if (isWord(prev)) return undefined;
    if (ch === "@") {
      if (query.startsWith(" ")) return undefined;
      return inCode(text, i) ? undefined : { kind: "mention", start: i, end: caret, query };
    }
    if (ch === "#") {
      if (prev === "&" || !CHANNEL_QUERY.test(query)) return undefined;
      return inCode(text, i) ? undefined : { kind: "channel", start: i, end: caret, query };
    }
    if (query.length < EMOJI_MIN_QUERY || !EMOJI_QUERY.test(query)) return undefined;
    return inCode(text, i) ? undefined : { kind: "emoji", start: i, end: caret, query };
  }
  return undefined;
}

/**
 * The menu for `trigger` with its ranked `items`, or undefined when it should stay closed: no emoji match, or an
 * `@` query with a space or punctuation that matches no one (the user is just writing). Otherwise an empty result
 * shows "No matches". The highlighted row survives a refresh of the same query and resets when the query changes.
 */
export function autocompleteMenu(trigger: AutocompleteTrigger, items: AutocompleteItem[], previous?: AutocompleteMenuState): AutocompleteMenuState | undefined {
  if (items.length === 0 && (trigger.kind === "emoji" || !NAMEISH.test(trigger.query))) return undefined;
  const same = previous !== undefined && previous.kind === trigger.kind && previous.start === trigger.start && previous.query === trigger.query;
  const active = same ? Math.min(previous.active, Math.max(0, items.length - 1)) : 0;
  return { kind: trigger.kind, start: trigger.start, end: trigger.end, query: trigger.query, items, active };
}

/** ↑/↓ through `count` rows, wrapping around. */
export function moveActive(active: number, count: number, delta: number): number {
  if (count <= 0) return 0;
  return (((active + delta) % count) + count) % count;
}

// ---------- Ranking ----------

type Ranked = { item: AutocompleteItem; score: number; affinity: number; order: number; key: string };

function byRank(a: Ranked, b: Ranked): number {
  return b.score - a.score || b.affinity - a.affinity || a.order - b.order || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0) || (a.item.id < b.item.id ? -1 : a.item.id > b.item.id ? 1 : 0);
}

function presenceOf(live: LiveState, m: Member): AvatarPresence | undefined {
  if (m.kind === "agent") return agentPresence(agentState(live, m).state);
  const p = live.presence[m.userId];
  return p === "active" ? "online" : p === "away" ? "away" : undefined;
}

/** People in the conversation (recent authors first, then DM participants), without me. */
function conversationIds(cache: WorkspaceCache, scope: MentionScope): string[] {
  const out: string[] = [];
  const add = (id: string) => {
    if (id !== cache.meId && !out.includes(id)) out.push(id);
  };
  for (const id of scope.recentAuthorIds) add(id);
  for (const id of cache.channels[scope.channelId]?.dmUserIds ?? []) add(id);
  return out;
}

/** Partners of my most recently active DMs / group DMs, most recent first. */
function recentDmPartners(cache: WorkspaceCache): string[] {
  const dms = Object.keys(cache.memberships)
    .map((id) => cache.channels[id])
    .filter((c): c is Channel => c !== undefined && (c.kind === "dm" || c.kind === "group_dm") && !c.archived)
    .toSorted((a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .slice(0, RECENT_DMS);
  const out: string[] = [];
  for (const c of dms) for (const id of c.dmUserIds ?? []) if (id !== cache.meId && !out.includes(id)) out.push(id);
  return out;
}

function sameName(a: string, b: string): boolean {
  return fold(a).trim() === fold(b).trim();
}

/**
 * `@` suggestions: people and agents I may see (`can.seeDirectory`), matched on display and full name, plus
 * @here / @channel / @everyone in channels when `can.mentionBroadcast` allows. Broadcast matches always keep a row.
 */
export function mentionItems(query: string, cache: WorkspaceCache, live: LiveState, scope: MentionScope): AutocompleteItem[] {
  const me = cache.members[cache.meId];
  const caps = me === undefined ? undefined : capabilitiesFor(me, scope.now);
  const filtered = me !== undefined && caps !== undefined && caps.seeDirectory !== "all";
  const conversation = conversationIds(cache, scope);
  const dms = recentDmPartners(cache);
  const people: Ranked[] = [];
  for (const id of Object.keys(cache.members)) {
    const m = cache.members[id]!;
    if (m.deactivated) continue;
    if (filtered && !canSeeMember(me!, m, sharedChannelIdsWith(cache, id), scope.now)) continue;
    const full = sameName(m.fullName, m.displayName) ? 0 : matchScore(query, m.fullName);
    let score = Math.max(matchScore(query, m.displayName), full);
    // Support (logic/support): "@support" and its stored name match too, whatever language its name shows in.
    if (isSupportMember(m)) {
      score = Math.max(score, matchScore(query, SUPPORT_HANDLE), matchScore(query, SUPPORT_STORED_NAME));
      if (score === 0 && matchesMention(query, m.displayName)) score = 1;
    }
    if (score === 0) continue;
    const inConversation = conversation.indexOf(id);
    const dm = dms.indexOf(id);
    const affinity = id === cache.meId ? 0 : inConversation >= 0 ? 3 : dm >= 0 ? 2 : 1;
    const order = inConversation >= 0 ? inConversation : dm >= 0 ? dm : 0;
    const item: AutocompleteItem = { kind: "user", id, label: m.displayName, isAgent: m.kind === "agent" };
    const detail = sameName(m.fullName, m.displayName) ? m.title : m.fullName;
    if (detail !== undefined && detail !== "") item.detail = detail;
    if (m.avatarUrl !== undefined) item.avatarUrl = m.avatarUrl;
    const org = organizationName(m);
    if (org !== undefined) item.organization = org;
    const presence = presenceOf(live, m);
    if (presence !== undefined) item.presence = presence;
    const status = activeStatus(m, scope.now);
    if (status !== undefined) item.status = status;
    people.push({ item, score, affinity, order, key: fold(m.displayName) });
  }
  // User groups (active ones), matched on handle and name, for people who see the whole directory.
  if (!filtered && me !== undefined) {
    for (const id of Object.keys(cache.userGroups ?? {})) {
      const g = cache.userGroups![id]!;
      if (!isGroupActive(g)) continue;
      const score = Math.max(matchScore(query, g.handle), matchScore(query, g.name));
      if (score === 0) continue;
      const item: AutocompleteItem = { kind: "group", id, label: `@${g.handle}`, name: g.name, memberCount: activeGroupMemberIds(g, cache.members).length };
      people.push({ item, score, affinity: g.memberIds.includes(cache.meId) ? 1 : 0, order: 0, key: g.handle });
    }
  }
  people.sort(byRank);

  const broadcasts: Ranked[] = [];
  const channel = cache.channels[scope.channelId];
  const inChannel = channel !== undefined && (channel.kind === "public" || channel.kind === "private");
  if (inChannel && caps !== undefined && caps.mentionBroadcast) {
    BROADCASTS.forEach((range, order) => {
      const score = matchScore(query, range);
      if (score > 0) broadcasts.push({ item: { kind: "broadcast", id: range, label: `@${range}`, description: BROADCAST_DESCRIPTIONS[range] }, score, affinity: 0, order, key: range });
    });
    broadcasts.sort(byRank);
  }

  // Merge by score (people first on ties), keeping a row for each matching broadcast.
  const kept = people.slice(0, Math.max(0, MAX_ITEMS - broadcasts.length));
  const out: AutocompleteItem[] = [];
  let p = 0;
  let b = 0;
  while ((p < kept.length || b < broadcasts.length) && out.length < MAX_ITEMS) {
    if (b >= broadcasts.length || (p < kept.length && kept[p]!.score >= broadcasts[b]!.score)) out.push(kept[p++]!.item);
    else out.push(broadcasts[b++]!.item);
  }
  return out;
}

/** `#` suggestions: channels I can see (members first; private or restricted-only channels need membership). */
export function channelItems(query: string, cache: WorkspaceCache): AutocompleteItem[] {
  const me = cache.members[cache.meId];
  const restricted = me !== undefined && isRestricted(me);
  const ranked: Ranked[] = [];
  for (const id of Object.keys(cache.channels)) {
    const c = cache.channels[id]!;
    if ((c.kind !== "public" && c.kind !== "private") || c.archived) continue;
    const member = cache.memberships[id] !== undefined;
    if (!member && (c.kind === "private" || restricted)) continue;
    const score = matchScore(query, c.name);
    if (score === 0) continue;
    const item: AutocompleteItem = { kind: "channel", id, label: c.name, isPrivate: c.kind === "private", external: c.external === true };
    if (c.topic !== undefined && c.topic !== "") item.detail = c.topic;
    ranked.push({ item, score, affinity: member ? 1 : 0, order: 0, key: fold(c.name) });
  }
  return ranked.toSorted(byRank).slice(0, MAX_ITEMS).map((r) => r.item);
}

/**
 * `:` suggestions: emoji whose shortcodes contain the query (prefix > word start > substring; no fuzzy tier, with ~1,900
 * shortcodes it is noise). The best-matching shortcode is shown; `recent` (unicode, newest first) wins ties.
 */
export function emojiItems(query: string, entries: EmojiEntry[], recent: string[]): AutocompleteItem[] {
  const ranked: Ranked[] = [];
  for (const e of entries) {
    let best = "";
    let score = 0;
    for (const name of e.names) {
      if (name.startsWith("skin-tone")) continue;
      const s = matchScore(query, name);
      if (s < SUBSTRING) continue;
      if (s > score || (s === score && s > 0 && name < best)) {
        score = s;
        best = name;
      }
    }
    if (score === 0) continue;
    const r = recent.indexOf(e.unicode);
    ranked.push({ item: { kind: "emoji", id: e.unicode, label: `:${best}:`, emoji: e.unicode }, score, affinity: r >= 0 ? 1 : 0, order: r >= 0 ? r : 0, key: best });
  }
  return ranked.toSorted(byRank).slice(0, MAX_ITEMS).map((r) => r.item);
}

/**
 * `/` suggestions: the channel's workflows (Slack lists them first), built-in commands, then app commands, matching the
 * query (catalogue order on ties).
 */
export function commandItems(query: string, appCommands: readonly AvailableCommand[] = [], workflows: readonly WorkflowShortcut[] = []): AutocompleteItem[] {
  const ranked: Ranked[] = [];
  workflows.forEach((w, i) => {
    const score = matchScore(query, w.name);
    if (score === 0) return;
    const item: AutocompleteItem = { kind: "workflow", id: w.id, label: w.name };
    if (w.icon !== undefined && w.icon !== "") item.icon = w.icon;
    if (w.description !== undefined && w.description !== "") item.description = w.description;
    ranked.push({ item, score, affinity: 1, order: i - workflows.length, key: fold(w.name) });
  });
  appCommands.forEach((c, i) => {
    const name = c.command.slice(1);
    if (SLASH_COMMANDS.some((b) => b.id === name)) return;
    const score = matchScore(query, name);
    if (score === 0) return;
    const item: AutocompleteItem = { kind: "appCommand", id: c.command, label: c.command, appName: c.appName, description: c.description };
    if (c.usageHint !== undefined && c.usageHint !== "") item.usageHint = c.usageHint;
    ranked.push({ item, score, affinity: 0, order: SLASH_COMMANDS.length + i, key: name });
  });
  SLASH_COMMANDS.forEach((c, order) => {
    const score = matchScore(query, c.id);
    if (score === 0) return;
    const item: AutocompleteItem = { kind: "command", id: c.id, label: `/${c.id}`, icon: c.icon, description: c.description };
    if (c.usage !== undefined) item.usage = c.usage;
    ranked.push({ item, score, affinity: 0, order, key: c.id });
  });
  return ranked.toSorted(byRank).slice(0, MAX_ITEMS).map((r) => r.item);
}

/** The usage line of the command the message starts with ("/remind " → its arguments; an app's usage hint). */
export function commandHint(text: string, appCommands: readonly AvailableCommand[] = []): CommandHint | undefined {
  const m = COMMAND_TEXT.exec(text);
  if (m === null) return undefined;
  const name = m[1]!.toLowerCase();
  const c = SLASH_COMMANDS.find((x) => x.id === name);
  if (c !== undefined) return c.usage === undefined ? undefined : { label: `/${c.id}`, usage: c.usage };
  const a = appCommands.find((x) => x.command === `/${name}`);
  return a?.usageHint === undefined || a.usageHint === "" ? undefined : { label: a.command, usage: msg("composer.command.appUsage", { usage: a.usageHint }) };
}

// ---------- Insertion ----------

/**
 * `@Display Name` when the server resolves that name to this member alone (no other active member has it as a
 * display or full name, and none has a longer name starting with it), else the unambiguous `<@userId>`.
 */
export function mentionToken(userId: string, members: Record<string, Member>): string {
  const m = members[userId];
  const name = m === undefined ? "" : m.displayName.trim();
  // Support's name is translated on this device only; the server knows it by id.
  if (name === "" || isSupportMember(m!)) return `<@${userId}>`;
  const lower = name.toLowerCase();
  for (const id of Object.keys(members)) {
    const o = members[id]!;
    if (id === userId || o.deactivated) continue;
    for (const other of [o.displayName, o.fullName]) {
      const n = other.trim().toLowerCase();
      if (n === lower || n.startsWith(`${lower} `)) return `<@${userId}>`;
    }
  }
  return `@${name}`;
}

/** `#name` when the server resolves it to this channel alone, else `<#channelId>`. */
export function channelToken(channelId: string, channels: Record<string, Channel>): string {
  const c = channels[channelId];
  if (c === undefined || !CHANNEL_NAME.test(c.name)) return `<#${channelId}>`;
  const lower = c.name.toLowerCase();
  for (const id of Object.keys(channels)) {
    const o = channels[id]!;
    if (id !== channelId && (o.kind === "public" || o.kind === "private") && o.name.toLowerCase() === lower) return `<#${channelId}>`;
  }
  return `#${c.name}`;
}

/** `@here` (or `<!here>` when a member is literally named "here", since member names win in the parser). */
export function broadcastToken(range: BroadcastRange, members: Record<string, Member>): string {
  for (const id of Object.keys(members)) {
    const m = members[id]!;
    if (!m.deactivated && (m.displayName.trim().toLowerCase() === range || m.fullName.trim().toLowerCase() === range)) return `<!${range}>`;
  }
  return `@${range}`;
}

/**
 * `@handle` for a user group, or Slack's `<!subteam^id>` when a member's name would win the parser's longest-name
 * match (a display or full name equal to the handle or starting with it and a space).
 */
export function groupToken(groupId: string, cache: WorkspaceCache): string {
  const g = cache.userGroups?.[groupId];
  if (g === undefined) return `<!subteam^${groupId}>`;
  const lower = g.handle.toLowerCase();
  for (const id of Object.keys(cache.members)) {
    const o = cache.members[id]!;
    if (o.deactivated) continue;
    for (const other of [o.displayName, o.fullName]) {
      const n = other.trim().toLowerCase();
      if (n === lower || n.startsWith(`${lower} `)) return `<!subteam^${groupId}>`;
    }
  }
  return `@${g.handle}`;
}

/** The text a chosen row inserts. Emoji insert the emoji itself, as the composer's emoji picker does. */
export function completionToken(item: AutocompleteItem, cache: WorkspaceCache): string {
  switch (item.kind) {
    case "user":
      return mentionToken(item.id, cache.members);
    case "broadcast":
      return broadcastToken(item.id, cache.members);
    case "group":
      return groupToken(item.id, cache);
    case "channel":
      return channelToken(item.id, cache.channels);
    case "emoji":
      return item.emoji;
    case "command":
    case "appCommand":
      return item.label;
    case "workflow":
      return "";
  }
}

/** Replaces [start, end) with `token` plus a space (reusing a following space) and puts the caret after it. */
export function applyCompletion(text: string, start: number, end: number, token: string): TextEdit {
  const after = text.slice(end);
  const space = isSpace(after.charAt(0)) ? "" : " ";
  return { text: `${text.slice(0, start)}${token}${space}${after}`, caret: start + token.length + 1 };
}

/** The composer's @ button: types `trigger` at the caret, after a space when it would touch a word. */
export function insertTrigger(text: string, caret: number, trigger: string): TextEdit {
  const at = Math.max(0, Math.min(caret, text.length));
  const before = text.slice(0, at);
  const pad = before === "" || isSpace(before.charAt(before.length - 1)) ? "" : " ";
  return { text: `${before}${pad}${trigger}${text.slice(at)}`, caret: at + pad.length + trigger.length };
}
